import "./mobile-cases.css";
import { useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";
import { NsButton } from "@/components/new-site/NewSiteChrome";
import { findCaseStudyArticle, type ArticleSection } from "./caseStudyArticles";
import { NewSiteSeo, articleSchema, breadcrumb, clip } from "@/components/new-site/seo";

function Section({ section }: { section: ArticleSection }) {
  return (
    <div className="flex w-full flex-col gap-[18px]">
      <h2 className="text-[24px] font-medium leading-[1.3] tracking-[-0.54px] md:text-[27px]">{section.heading}</h2>
      {section.paragraphs?.map((p, i) => <p key={i} className="text-[16px] leading-[1.5] md:text-[18px]">{p}</p>)}
      {section.bullets && (
        <ul className="list-disc ps-[27px] text-[16px] leading-[1.5] md:text-[18px]">
          {section.bullets.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      )}
    </div>
  );
}

export default function NewSiteCaseStudyArticle({ onContact }: { onContact?: () => void }) {
  const { slug } = useParams();
  const article = findCaseStudyArticle(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [article]);

  if (!article) return <Navigate to="/case-studies" replace />;

  const path = `/case-studies/${article.slug}`;
  const description = clip(article.summary);

  return (
    <article className="ns-cases ns-cases-article bg-ns-white text-ns-plum">
      <NewSiteSeo
        path={path}
        type="article"
        title={`${article.title} | Snapshot Live`}
        description={description}
        schema={[
          articleSchema(article.title, description, path),
          breadcrumb([["Home", "/"], ["Case Studies", "/case-studies"], [article.title, path]]),
        ]}
      />
      <img src={article.heroImage} alt={`Snapshot Live camera image from the ${article.title.split(":")[0]} case study`} style={{ objectPosition: article.heroPosition ?? "center" }} className="h-[220px] w-full object-cover md:h-[329px]" />

      <header className="mx-auto flex max-w-[900px] flex-col gap-[21px] px-6 py-14 md:box-content md:px-[72px] md:py-[81px]">
        <div className="flex flex-col gap-[11px]">
          <svg aria-hidden viewBox="0 0 36.6748 32.1016" className="h-8 w-[37px] text-ns-plum" fill="currentColor"><path d="M27.9746 30.2646L26.9082 32.1016L17.582 26.6865L18.6484 24.8496L27.9746 30.2646ZM11.1152 20.8799L11.0713 31.6641L8.94629 31.6553L8.99023 20.8721L11.1152 20.8799ZM36.6748 16.6143L27.3105 21.9619L26.2568 20.1172L35.6221 14.7695L36.6748 16.6143ZM10.416 12.6279L1.05469 17.9805L0 16.1357L9.3623 10.7842L10.416 12.6279ZM27.4512 0.705078L27.4434 11.4893L25.3184 11.4873L25.3271 0.703125L27.4512 0.705078ZM18.541 5.78516L17.4014 7.57715L8.2998 1.79297L9.43945 0L18.541 5.78516Z" /></svg>
          <h1 className="text-[32px] font-medium leading-[1.15] md:text-[40px]">{article.title}</h1>
          <p className="text-[15px] font-medium leading-[1.5] text-ns-plum/50">{article.intro}</p>
        </div>
        <div className="h-px w-full bg-ns-plum/50" />
        <div className="flex items-center justify-between gap-4">
          <p className="text-[12px] font-medium leading-[19.1px] text-ns-plum/50">{article.date}</p>
          <span className="flex h-6 items-center bg-ns-lime px-5 text-[12px] font-medium leading-[19.1px]">{article.topic}</span>
        </div>
      </header>

      <div className="mx-auto flex max-w-[900px] flex-col gap-12 px-6 pb-[90px] md:box-content md:px-[72px]">
        {article.sectionsBeforeImage.map((s, i) => <Section key={`b${i}`} section={s} />)}
        {article.inlineImage && (
          <figure className="flex flex-col gap-3">
            <img src={article.inlineImage.src} alt={article.inlineImage.alt} loading="lazy" className="h-[240px] w-full object-cover md:h-[390px]" />
            <figcaption className="text-[12px] font-medium leading-[19.1px] text-ns-plum/50">{article.inlineImage.caption}</figcaption>
          </figure>
        )}
        {article.sections.map((s, i) => <Section key={i} section={s} />)}
        {article.closing && <p className="text-[24px] font-medium leading-[1.3] tracking-[-0.54px] md:text-[27px]">{article.closing}</p>}
      </div>

      <div className="flex justify-center pb-[110px] pt-6">
        <NsButton to="/case-studies">See More Stories</NsButton>
      </div>

      <section aria-labelledby="case-study-cta" className="ns-news-cta-bg flex flex-col items-center gap-8 px-6 py-20 text-center md:px-[72px] md:py-[120px]">
        <div className="flex w-full max-w-[720px] flex-col items-center gap-4">
          <h2 id="case-study-cta" className="text-[32px] font-medium leading-[1.15] md:text-[40px]">Secure Your Operational Visibility Today</h2>
          <p className="max-w-[536px] text-[15px] font-medium leading-[1.5]">Speak with our team to discover how autonomous, image-based monitoring can improve visibility, reduce costs and help you make smarter, faster decisions.</p>
        </div>
        <div className="flex flex-wrap justify-center gap-4">
          <NsButton variant="lime" onClick={onContact}>Contact Us</NsButton>
          <NsButton onClick={onContact}>Request Brochure</NsButton>
        </div>
      </section>
    </article>
  );
}
